import { BOOKING_OPPORTUNITY_ICON } from "@/features/bookings/opportunityIcon";
import {
  Activity,
  AlertTriangle,
  Anchor,
  BarChart3,
  CalendarClock,
  CalendarSearch,
  ClipboardCheck,
  Globe,
  Heading,
  Link2,
  Minus,
  Newspaper,
  Package,
  Search,
  Ship,
  Target,
  Timer,
  type LucideIcon,
} from "lucide-react";

import type { GridWidget } from "@/features/dashboard/grid/layoutUtils";
import type { HomeWidget, HomeWidgetType } from "@/types/home";

/** Widget types that render structurally (no data fetch, single row tall). */
export const LAYOUT_HOME_TYPES: HomeWidgetType[] = [
  "section_title",
  "section_divider",
];

export function isLayoutHomeWidget(widget: HomeWidget): boolean {
  return LAYOUT_HOME_TYPES.includes(widget.widget_type);
}

interface HomeWidgetTypeDef {
  type: HomeWidgetType;
  /** Unique palette id when several entries share a `type` (e.g. queues). */
  id?: string;
  label: string;
  description: string;
  icon: LucideIcon;
  defaultTitle: string;
  defaultSpan: { col: number; row: number };
  /** Whether adding this type opens the metric builder sheet. */
  needsBuilder?: boolean;
  /** Seed config merged into the widget when added from the palette. */
  defaultConfig?: Record<string, unknown>;
}

/**
 * Catalog of built-in Home widget types offered in the palette. `metric` is the
 * only type that needs the builder sheet (to pick an entity/KPI/visualization);
 * the operational widgets are added directly with sensible defaults. Rate
 * lookup, schedule lookup, and the Kai briefing are optional (palette + grid
 * gated by feature flags).
 */
export const HOME_WIDGET_TYPES: HomeWidgetTypeDef[] = [
  {
    type: "kai_briefing",
    label: "Kai briefing",
    description: "Today's LLM-generated operations brief from Kai.",
    icon: Newspaper,
    defaultTitle: "Today's briefing",
    defaultSpan: { col: 4, row: 4 },
  },
  {
    type: "opportunities",
    label: "Top opportunities",
    description: "Rebook and first-book opportunities ranked by savings or quote.",
    icon: BOOKING_OPPORTUNITY_ICON,
    defaultTitle: "Top opportunities",
    defaultSpan: { col: 4, row: 5 },
  },
  {
    type: "feed_list",
    label: "Activity feed",
    description: "Live operational events from your workspace.",
    icon: Activity,
    defaultTitle: "Activity feed",
    defaultSpan: { col: 2, row: 6 },
  },
  {
    type: "booking_queue",
    id: "queue-pending-book",
    label: "Needs booking",
    description: "Bookings awaiting an operations booking action.",
    icon: ClipboardCheck,
    defaultTitle: "Needs booking",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "pending_book", limit: 8 },
  },
  {
    type: "booking_queue",
    id: "queue-rolled-delayed",
    label: "Rolled / delayed",
    description: "Disrupted bookings that need urgent attention.",
    icon: AlertTriangle,
    defaultTitle: "Rolled / delayed",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "rolled_delayed", limit: 8 },
  },
  {
    type: "booking_queue",
    id: "queue-cutoff-soon",
    label: "Cutoffs approaching",
    description: "Bookings whose cargo cutoff is near.",
    icon: Timer,
    defaultTitle: "Cutoffs approaching",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "cutoff_soon", limit: 8 },
  },
  {
    type: "booking_queue",
    id: "queue-stuffing-soon",
    label: "Stuffing approaching",
    description: "Bookings with stuffing dates coming up.",
    icon: Package,
    defaultTitle: "Stuffing approaching",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "stuffing_soon", limit: 8 },
  },
  {
    type: "booking_queue",
    id: "queue-departure-soon",
    label: "Departures approaching",
    description: "Bookings departing within the next week.",
    icon: Ship,
    defaultTitle: "Departures approaching",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "departure_soon", limit: 8 },
  },
  {
    type: "booking_queue",
    id: "queue-arrival-soon",
    label: "Arrivals approaching",
    description: "Bookings arriving within the next two weeks.",
    icon: Anchor,
    defaultTitle: "Arrivals approaching",
    defaultSpan: { col: 2, row: 4 },
    defaultConfig: { preset: "arrival_soon", limit: 8 },
  },
  {
    type: "schedule",
    label: "Schedule snapshot",
    description: "This week's departures, arrivals, cutoffs and stuffing.",
    icon: CalendarClock,
    defaultTitle: "Schedule snapshot",
    defaultSpan: { col: 4, row: 5 },
  },
  {
    type: "rate_lookup",
    label: "Rate lookup",
    description: "Search contracted and spot rates by lane.",
    icon: Search,
    defaultTitle: "Rate lookup",
    defaultSpan: { col: 4, row: 6 },
  },
  {
    type: "schedule_lookup",
    label: "Schedule lookup",
    description: "Search sailings by lane — list or calendar.",
    icon: CalendarSearch,
    defaultTitle: "Schedule lookup",
    defaultSpan: { col: 4, row: 6 },
  },
  {
    type: "tracking_lookup",
    label: "Tracking lookup",
    description:
      "Track a voyage by B/L, booking ref, source id, file id, or vessel — globe, POL/POD, and events.",
    icon: Globe,
    defaultTitle: "Tracking lookup",
    defaultSpan: { col: 4, row: 6 },
  },
  {
    type: "sales_quota",
    label: "Sales quota",
    description: "TEU / revenue quota progress for the logged-in sales person.",
    icon: Target,
    defaultTitle: "My sales quota",
    defaultSpan: { col: 2, row: 4 },
  },
  {
    type: "metric",
    label: "Metric / chart",
    description: "A KPI counter, chart, or table from the metrics catalog.",
    icon: BarChart3,
    defaultTitle: "New metric",
    defaultSpan: { col: 2, row: 4 },
    needsBuilder: true,
  },
  {
    type: "section_title",
    label: "Section title",
    description: "A heading to group widgets.",
    icon: Heading,
    defaultTitle: "Section",
    defaultSpan: { col: 4, row: 1 },
  },
  {
    type: "section_divider",
    label: "Divider",
    description: "A horizontal rule between sections.",
    icon: Minus,
    defaultTitle: "",
    defaultSpan: { col: 4, row: 1 },
  },
  {
    type: "quick_link",
    label: "Quick link",
    description:
      "Shortcut to a page — defaults to wherever you visited last (filters included).",
    icon: Link2,
    defaultTitle: "Quick link",
    defaultSpan: { col: 1, row: 1 },
  },
];

/** Default column/row spans per metric visualization (matches reports). */
const METRIC_SPANS: Record<string, { col: number; row: number }> = {
  counter: { col: 1, row: 2 },
  bar_chart: { col: 2, row: 4 },
  line_chart: { col: 2, row: 4 },
  mixed_chart: { col: 2, row: 4 },
  pie_chart: { col: 2, row: 4 },
  table: { col: 2, row: 4 },
  heatmap: { col: 2, row: 5 },
};

/** Default span for a widget, accounting for metric visualization. */
export function defaultHomeSpan(widget: HomeWidget): { col: number; row: number } {
  if (widget.widget_type === "metric") {
    const viz = String(widget.config.visualization ?? "");
    return METRIC_SPANS[viz] ?? { col: 2, row: 4 };
  }
  const def = HOME_WIDGET_TYPES.find((t) => t.type === widget.widget_type);
  return def?.defaultSpan ?? { col: 2, row: 4 };
}

/**
 * Minimum row span for a widget. Structural widgets are a single row tall; line
 * charts need at least 4 rows so their time-series axes render legibly (they
 * look cramped at the generic 2-row minimum).
 */
export function minHomeRowSpan(widget: HomeWidget): number {
  if (isLayoutHomeWidget(widget)) return 1;
  if (widget.widget_type === "quick_link") return 1;
  if (
    widget.widget_type === "metric" &&
    (String(widget.config.visualization ?? "") === "line_chart" ||
      String(widget.config.visualization ?? "") === "mixed_chart" ||
      String(widget.config.visualization ?? "") === "heatmap")
  ) {
    return 4;
  }
  if (
    widget.widget_type === "rate_lookup" ||
    widget.widget_type === "schedule_lookup" ||
    widget.widget_type === "tracking_lookup" ||
    widget.widget_type === "schedule"
  ) {
    return 4;
  }
  if (widget.widget_type === "kai_briefing") {
    return 3;
  }
  if (widget.widget_type === "opportunities") {
    return 1;
  }
  return 2;
}

/**
 * Mobile display-only minimum row spans for complex lookup tiles. Applied when
 * stacking the home grid on a narrow viewport — does not mutate persisted layout.
 */
const MOBILE_HARD_MIN_ROW_SPAN: Partial<Record<HomeWidgetType, number>> = {
  rate_lookup: 9,
  schedule_lookup: 9,
  tracking_lookup: 9,
};

export function mobileHardMinHomeRowSpan(widget: HomeWidget): number | undefined {
  return MOBILE_HARD_MIN_ROW_SPAN[widget.widget_type];
}

/** Map widget keys to mobile hard min rows (for `stackLayoutForMobile`). */
export function mobileHardMinRowByWidgetKey(
  widgets: HomeWidget[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const w of widgets) {
    const min = mobileHardMinHomeRowSpan(w);
    if (min != null) out[w.widget_key] = min;
  }
  return out;
}

/** Map a Home widget onto the generic grid packer's minimal shape. */
export function homeWidgetToGridWidget(widget: HomeWidget): GridWidget {
  const span = defaultHomeSpan(widget);
  const minRow = minHomeRowSpan(widget);
  return {
    widget_key: widget.widget_key,
    column_span: widget.column_span || span.col,
    // Never let a chart render below its minimum height, even if an older
    // persisted layout stored a smaller row span.
    row_span: Math.max(minRow, widget.row_span || span.row),
    min_row_span: minRow,
  };
}

/** Generate a client-side widget key. */
export function newHomeWidgetKey(): string {
  return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Role-template key for the "Bookings this week" counter. */
export const HOME_BOOKINGS_THIS_WEEK_KEY = "home-bookings-count";

/**
 * Older templates stored this tile with empty filters, so it counted every
 * booking. Re-seed the dynamic `this_week` ETD window when the tile still has
 * no date scope. Safe to call on every layout load.
 */
export function hydrateBookingsThisWeekFilter(widget: HomeWidget): HomeWidget {
  if (widget.widget_key !== HOME_BOOKINGS_THIS_WEEK_KEY) return widget;
  const filters =
    (widget.config.filters as Record<string, unknown> | undefined) ?? {};
  const dateRange =
    typeof filters.date_range === "string" ? filters.date_range.trim() : "";
  if (dateRange || filters.date_from != null || filters.date_to != null) {
    return widget;
  }
  return {
    ...widget,
    config: {
      ...widget.config,
      filters: { ...filters, date_range: "this_week" },
    },
  };
}

/** Home widget types withheld from Client (consignee portal) users. */
const CLIENT_RESTRICTED_HOME_WIDGET_TYPES = new Set<HomeWidgetType>([
  "rate_lookup",
  "schedule_lookup",
  "opportunities",
  "kai_briefing",
]);

export function filterHomeWidgetsForRole(
  widgets: HomeWidget[],
  role?: string | null,
): HomeWidget[] {
  if (role !== "Client") return widgets;
  return widgets.filter((widget) => {
    if (CLIENT_RESTRICTED_HOME_WIDGET_TYPES.has(widget.widget_type)) {
      return false;
    }
    if (widget.widget_type === "metric") {
      const entity = String(widget.config.entity ?? "");
      if (
        entity === "rebooks" ||
        entity === "recommendations" ||
        entity === "search_cycles"
      ) {
        return false;
      }
    }
    return true;
  });
}

export function hydrateHomeWidgets(widgets: HomeWidget[]): HomeWidget[] {
  return widgets.map(hydrateBookingsThisWeekFilter);
}
